import { z } from "zod";
import { SeverityTierSchema } from "./severity-tier";

// --- Shared Schemas ---

const runStatusSchema = z.enum(["pending", "running", "completed", "failed"]);

// --- Batch Schemas ---

export const StatusBreakdownSchema = z.object({
  tier: SeverityTierSchema,
  count: z.number(),
  percentage: z.number(),
});
export type StatusBreakdown = z.infer<typeof StatusBreakdownSchema>;

export const KpiCardsSchema = z.object({
  overall_match_rate: z.number(),
  completion_rate: z.number(),
  completed_match_rate: z.number(),
});
export type KpiCards = z.infer<typeof KpiCardsSchema>;

export const ComponentVarianceSummarySchema = z.object({
  component: z.string(),
  affected_packets: z.number(),
  total_dollar_impact: z.number(),
  average_variance: z.number(),
});
export type ComponentVarianceSummary = z.infer<
  typeof ComponentVarianceSummarySchema
>;

export const ErrorRateByTypeSchema = z.object({
  error_type: z.string(),
  count: z.number(),
  percentage: z.number(),
});
export type ErrorRateByType = z.infer<typeof ErrorRateByTypeSchema>;

export const ErrorSummarySchema = z.object({
  dlq_count: z.number(),
  error_rate_by_type: z.array(ErrorRateByTypeSchema),
});
export type ErrorSummary = z.infer<typeof ErrorSummarySchema>;

export const ShardSummarySchema = z.object({
  shard_id: z.string(),
  status: runStatusSchema,
  total_packets: z.number(),
  processed_packets: z.number(),
  match_rate: z.number(),
  status_breakdown: z.array(StatusBreakdownSchema),
});
export type ShardSummary = z.infer<typeof ShardSummarySchema>;

export const BatchSummarySchema = z.object({
  batch_id: z.string(),
  org_category: z.string(),
  org_id: z.string(),
  total_packets: z.number(),
  status: runStatusSchema,
  match_rate: z.number(),
  status_breakdown: z.array(StatusBreakdownSchema),
  duration_seconds: z.number().nullable(),
  completed_at: z.string().nullable(),
  created_at: z.string(),
  has_ai_analysis: z.boolean(),
});
export type BatchSummary = z.infer<typeof BatchSummarySchema>;

export const BatchDetailSchema = BatchSummarySchema.extend({
  kpi_cards: KpiCardsSchema,
  top_variance_components: z.array(ComponentVarianceSummarySchema),
  error_summary: ErrorSummarySchema,
  shard_summaries: z.array(ShardSummarySchema).nullable(),
  ai_summary: z.string().nullable(),
});
export type BatchDetail = z.infer<typeof BatchDetailSchema>;

/** Flexible schema for the run detail screen — matches actual backend BatchSummary response. */
export const RunDetailSchema = z.object({
  batch_id: z.string(),
  org_category: z.string(),
  org_id: z.string(),
  directory_path: z.string().optional(),
  status: runStatusSchema,
  total_packets: z.number(),
  completed_count: z.number().optional(),
  match_count: z.number().optional(),
  variance_count: z.number().optional(),
  error_count: z.number().optional(),
  match_rate: z.number().nullable(),
  status_breakdown: z.array(StatusBreakdownSchema).optional(),
  duration_seconds: z.number().nullable().optional(),
  created_at: z.string(),
  started_at: z.string().nullable().optional(),
  completed_at: z.string().nullable(),
  workflow_id: z.string().optional(),
  has_ai_analysis: z.boolean().optional(),
  expires_at: z.string().optional(),
  failed_docs: z.array(z.string()).optional(),
  persistence_failures: z.number().optional(),
  persistence_failed_docs: z.array(z.string()).optional(),
  // Extended fields (present in BatchDetail responses)
  kpi_cards: KpiCardsSchema.optional(),
  top_variance_components: z.array(ComponentVarianceSummarySchema).optional(),
  error_summary: ErrorSummarySchema.optional(),
  shard_summaries: z.array(ShardSummarySchema).nullable().optional(),
  ai_summary: z.string().nullable().optional(),
});
export type RunDetail = z.infer<typeof RunDetailSchema>;

// --- Packet Schemas ---

export const ComparisonRowSchema = z.object({
  field: z.string(),
  label: z.string().optional(),
  fsc_value: z.number(),
  pacific_value: z.number(),
  difference: z.number(),
  percentage_difference: z.number(),
});
export type ComparisonRow = z.infer<typeof ComparisonRowSchema>;

export const VarianceDetailSchema = z.object({
  field: z.string(),
  label: z.string().optional(),
  fsc_value: z.number(),
  pacific_value: z.number(),
  difference: z.number(),
  percentage_difference: z.number(),
  is_expected: z.boolean(),
  expected_reason: z.string().nullable(),
});
export type VarianceDetail = z.infer<typeof VarianceDetailSchema>;

export const PacketResultSchema = z.object({
  packet_id: z.string(),
  batch_id: z.string(),
  severity_tier: SeverityTierSchema,
  fsc_gross: z.number(),
  fsc_paye: z.number(),
  fsc_net: z.number(),
  pacific_gross: z.number(),
  pacific_paye: z.number(),
  pacific_net: z.number(),
  variance_field_count: z.number(),
  // Grouping fields (optional for backward compatibility)
  pay_group_code: z.string().nullish(),
  pay_group_name: z.string().nullish(),
  pay_period_start: z.string().nullish(),
  pay_period_end: z.string().nullish(),
  payee_id: z.string().nullish(),
});
export type PacketResult = z.infer<typeof PacketResultSchema>;

// --- Facets Schemas (for filter dropdown population) ---

export const PayGroupFacetSchema = z.object({
  code: z.string(),
  name: z.string(),
});
export type PayGroupFacet = z.infer<typeof PayGroupFacetSchema>;

export const PayPeriodFacetSchema = z.object({
  start: z.string(),
  end: z.string(),
});
export type PayPeriodFacet = z.infer<typeof PayPeriodFacetSchema>;

export const PacketFacetsSchema = z.object({
  pay_groups: z.array(PayGroupFacetSchema),
  pay_periods: z.array(PayPeriodFacetSchema),
});
export type PacketFacets = z.infer<typeof PacketFacetsSchema>;

export const PacketDetailSchema = PacketResultSchema.extend({
  comparison_rows: z.array(ComparisonRowSchema),
  variance_details: z.array(VarianceDetailSchema),
  ai_analysis: z.string().nullable(),
  // Component arrays — passthrough until schemas are defined by backend contract
  fsc_components: z.array(z.unknown()).optional(),
  component_mappings: z.array(z.unknown()).optional(),
  pacific_input_components: z.array(z.unknown()).optional(),
  pacific_output_components: z.array(z.unknown()).optional(),
});
export type PacketDetail = z.infer<typeof PacketDetailSchema>;

// --- Calculation Graph Schemas ---

export const PacketGraphNodeSchema = z.object({
  node_id: z.string(),
  label: z.string(),
  node_class: z.string(),
  node_type: z.string(),
  calculated_amount: z.number(),
  calculation_description: z.string(),
  has_variance: z.boolean(),
  variance_amount: z.number().nullable(),
  has_value: z.boolean(),
  per_day_calculated_amount: z.record(z.string(), z.number()),
  execution_order_index: z.number().nullable(),
});
export type PacketGraphNode = z.infer<typeof PacketGraphNodeSchema>;

export const PacketGraphEdgeSchema = z.object({
  source_node_id: z.string(),
  target_node_id: z.string(),
});
export type PacketGraphEdge = z.infer<typeof PacketGraphEdgeSchema>;

export const DataCompletenessSchema = z.object({
  has_graph_data: z.boolean(),
  has_per_day_amounts: z.boolean(),
  has_execution_order: z.boolean(),
});
export type DataCompleteness = z.infer<typeof DataCompletenessSchema>;

export const PacketGraphDetailSchema = z.object({
  batch_id: z.string(),
  packet_id: z.string(),
  root_node_id: z.string().nullable(),
  root_node_ids: z.array(z.string()),
  leaf_node_ids: z.array(z.string()),
  nodes: z.array(PacketGraphNodeSchema),
  edges: z.array(PacketGraphEdgeSchema),
  execution_ordered_node_ids: z.array(z.string()),
  data_completeness: DataCompletenessSchema,
});
export type PacketGraphDetail = z.infer<typeof PacketGraphDetailSchema>;

/** @deprecated Use PacketGraphNode instead */
export type CalculationNode = PacketGraphNode;
/** @deprecated Use PacketGraphDetailSchema instead */
export const CalculationGraphSchema = PacketGraphDetailSchema;
/** @deprecated Use PacketGraphDetail instead */
export type CalculationGraph = PacketGraphDetail;

// --- Sankey Schemas ---

export const SankeyNodeSchema = z.object({
  id: z.string(),
  label: z.string(),
  packet_count: z.number(),
  dollar_amount: z.number(),
  severity_tier: SeverityTierSchema,
});
export type SankeyNode = z.infer<typeof SankeyNodeSchema>;

export const SankeyLinkSchema = z.object({
  source: z.string(),
  target: z.string(),
  packet_count: z.number(),
  dollar_amount: z.number(),
  severity_tier: SeverityTierSchema,
});
export type SankeyLink = z.infer<typeof SankeyLinkSchema>;

export const SankeyDataSchema = z.object({
  nodes: z.array(SankeyNodeSchema),
  links: z.array(SankeyLinkSchema),
  ai_summary: z.string().nullable(),
  top_variance_sources: z.array(z.string()),
});
export type SankeyData = z.infer<typeof SankeyDataSchema>;

// --- Historical Schemas ---

export const HistoricalDataPointSchema = z.object({
  batch_id: z.string(),
  date: z.string(),
  overall_match_rate: z.number(),
  total_variance_dollars: z.number(),
  total_packets: z.number(),
  top_variance_components: z.array(z.string()),
});
export type HistoricalDataPoint = z.infer<typeof HistoricalDataPointSchema>;

// --- Trigger/Progress Schemas ---

/** Standard scale: single-batch shadow run (workflow discriminator: "shadow_run") */
export const ShadowRunInputSchema = z.object({
  workflow: z.literal("shadow_run"),
  org_category: z.string(),
  org_id: z.string(),
  directory_path: z.string().min(1),
  batch_id: z.string().nullable().optional(),
  doc_ids: z.array(z.string()).nullable().optional(),
  timeout_minutes: z.number().int().optional(),
  persist_batch_summary: z.boolean().optional(),
  parent_orchestrator_id: z.string().nullable().optional(),
  circuit_breaker_threshold: z.number().min(0).max(1).optional(),
  circuit_breaker_sample_size: z.number().int().min(10).optional(),
  trigger_analysis: z.boolean().optional(),
  analysis_system_prompt: z.string().nullable().optional(),
});
export type ShadowRunInput = z.infer<typeof ShadowRunInputSchema>;

/** Enterprise scale: hierarchical sharded orchestration (workflow discriminator: "shadow_run_orchestrator") */
export const ShadowRunOrchestratorInputSchema = z.object({
  workflow: z.literal("shadow_run_orchestrator"),
  org_category: z.string(),
  org_id: z.string(),
  directory_path: z.string().min(1),
  batch_id: z.string().nullable().optional(),
  shard_size: z.number().int().min(100).max(10000).optional(),
  max_parallel_shards: z.number().int().min(1).nullable().optional(),
  timeout_minutes: z.number().int().min(1).optional(),
  persist_batch_summary: z.boolean().optional(),
  circuit_breaker_threshold: z.number().min(0).max(1).optional(),
  circuit_breaker_sample_size: z.number().int().min(1).optional(),
  trigger_analysis: z.boolean().optional(),
  analysis_system_prompt: z.string().nullable().optional(),
});
export type ShadowRunOrchestratorInput = z.infer<
  typeof ShadowRunOrchestratorInputSchema
>;

export const InputPayloadSchema = z.discriminatedUnion("workflow", [
  ShadowRunInputSchema,
  ShadowRunOrchestratorInputSchema,
]);
export type InputPayload = z.infer<typeof InputPayloadSchema>;

export const AgentInvocationRequestSchema = z.object({
  service_account_id: z.string(),
  input_payload: InputPayloadSchema,
});
export type AgentInvocationRequest = z.infer<
  typeof AgentInvocationRequestSchema
>;

export const AgentBlueprintSchema = z.object({
  name: z.string(),
  description: z.string().default(""),
  version: z.string().default(""),
});
export type AgentBlueprint = z.infer<typeof AgentBlueprintSchema>;

export const AgentDetailSchema = z.object({
  id: z.string(),
  blueprint: AgentBlueprintSchema,
  is_active: z.boolean().optional(),
});
export type AgentDetail = z.infer<typeof AgentDetailSchema>;

export const AgentServiceAccountSchema = z.object({
  id: z.string(),
  agent_id: z.string(),
  workspace_id: z.string(),
  description: z.string().default(""),
  is_active: z.boolean().default(true),
});
export type AgentServiceAccount = z.infer<typeof AgentServiceAccountSchema>;

export const RunProgressSchema = z.object({
  workflow_id: z.string(),
  status: runStatusSchema,
  total_packets: z.number(),
  processed_packets: z.number(),
  match_rate: z.number(),
  status_breakdown: z.array(StatusBreakdownSchema),
  error_rate: z.number(),
  circuit_breaker_state: z.enum(["ARMED", "SAFE", "TRIPPED"]),
  shard_summaries: z.array(ShardSummarySchema).nullable(),
  failed_workflow_ids: z.array(z.string()),
  batch_id: z.string().nullable(),
  started_at: z.string().nullable(),
  completed_at: z.string().nullable(),
  // Extended fields from invocation result
  message: z.string().nullable().optional(),
  directory_path: z.string().nullable().optional(),
  org_category: z.string().nullable().optional(),
  org_id: z.string().nullable().optional(),
  completion_rate: z.number().optional(),
  completed_match_rate: z.number().optional(),
  skipped_count: z.number().optional(),
  dlq_count: z.number().optional(),
  processing_duration_seconds: z.number().nullable().optional(),
});
export type RunProgress = z.infer<typeof RunProgressSchema>;

// --- Invocation Envelope Schemas ---

const invocationStatusSchema = z.enum(["PENDING", "RUNNING", "COMPLETED", "FAILED", "TERMINATED"]);

export const InvocationResultSchema = z
  .object({
    status: runStatusSchema,
    message: z.string().nullish(),
    batch_id: z.string().nullable().optional(),
    shadow_run_id: z.string().nullable().optional(),
    org_category: z.string().optional(),
    org_id: z.string().optional(),
    directory_path: z.string().optional(),
    total_packets: z.number(),
    match_count: z.number(),
    acceptable_count: z.number(),
    minor_count: z.number(),
    variance_count: z.number(),
    critical_count: z.number(),
    error_count: z.number(),
    overall_match_rate: z.number(),
    overall_completion_rate: z.number().optional(),
    completed_match_rate: z.number().optional(),
    component_variances: z.array(z.unknown()).optional(),
    skipped_count: z.number(),
    dlq_count: z.number().optional(),
    error_rate_by_type: z.record(z.number()).optional(),
    processing_duration_seconds: z.number(),
    completed_at: z.string().nullable(),
    analysis_workflow_id: z.string().nullable().optional(),
  })
  .transform(({ shadow_run_id, batch_id, ...rest }) => ({
    ...rest,
    batch_id: batch_id ?? shadow_run_id ?? null,
  }));
export type InvocationResult = z.infer<typeof InvocationResultSchema>;

export const InvocationResponseSchema = z.object({
  workflow_id: z.string(),
  run_id: z.string(),
  status: invocationStatusSchema,
  result: InvocationResultSchema.nullable(),
  error: z.unknown().nullable(),
});
export type InvocationResponse = z.infer<typeof InvocationResponseSchema>;

// --- Shadow Runs List View (paginated) ---

export const MatchRateStatusSchema = z.enum([
  "excellent",
  "good",
  "warning",
  "critical",
]);
export type MatchRateStatus = z.infer<typeof MatchRateStatusSchema>;

export const SortByFieldSchema = z.enum([
  "completedAt",
  "matchRate",
  "durationInSeconds",
]);
export type SortByField = z.infer<typeof SortByFieldSchema>;

export const SortDirectionSchema = z.enum(["asc", "desc"]);
export type SortDirection = z.infer<typeof SortDirectionSchema>;

export const ShadowRunListItemSchema = z.object({
  id: z.string(),
  workflowId: z.string().optional(),
  batchId: z.string().nullable().optional(),
  status: z.string(),
  completedAt: z.string().nullable(),
  totalPackets: z.number(),
  durationInSeconds: z.number(),
  matchRate: z.number(),
  matchRateStatus: MatchRateStatusSchema,
});
export type ShadowRunListItem = z.infer<typeof ShadowRunListItemSchema>;

export const ShadowRunsListResponseSchema = z.object({
  data: z.array(ShadowRunListItemSchema),
  nextPageCursor: z.string().nullable(),
});
export type ShadowRunsListResponse = z.infer<
  typeof ShadowRunsListResponseSchema
>;

export interface RunListFilters {
  sortBy: SortByField;
  sortDirection: SortDirection;
  status: string[];
  dateFrom: string | null;
  dateTo: string | null;
}

export const DEFAULT_RUN_LIST_FILTERS: RunListFilters = {
  sortBy: "completedAt",
  sortDirection: "desc",
  status: [],
  dateFrom: null,
  dateTo: null,
};

// --- Dead Letter Queue Schemas ---

export const DeadLetterEntrySchema = z.object({
  doc_id: z.string(),
  packet_id: z.string(),
  error_type: z.string(),
  error_message: z.string(),
  created_at: z.string(),
  retry_count: z.number(),
});
export type DeadLetterEntry = z.infer<typeof DeadLetterEntrySchema>;

export const PaginatedDeadLetterResponseSchema = z.object({
  items: z.array(DeadLetterEntrySchema),
  total: z.number(),
  page: z.number(),
  page_size: z.number(),
  total_pages: z.number(),
});
export type PaginatedDeadLetterResponse = z.infer<
  typeof PaginatedDeadLetterResponseSchema
>;
